// @ts-nocheck
import { useState, useEffect, useRef } from "react";
import { Html5QrcodeScanner } from "html5-qrcode";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, XCircle, Camera, Keyboard } from "lucide-react";

interface ScanResult {
  success: boolean;
  type?: string;
  error?: string;
  ticket_name?: string;
  extra_name?: string;
  registrant_data?: any;
  redeemed_at?: string;
}

export function QRScannerV2() {
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [manualCode, setManualCode] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [mode, setMode] = useState<"camera" | "manual">("manual");
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);

  useEffect(() => {
    if (mode === "camera" && isScanning) {
      scannerRef.current = new Html5QrcodeScanner(
        "qr-reader",
        { fps: 10, qrbox: { width: 250, height: 250 } },
        false
      );

      scannerRef.current.render(
        (decodedText) => {
          handleScan(decodedText);
          scannerRef.current?.clear();
          setIsScanning(false);
        },
        (error) => {
          // Ignore scan errors (no QR found yet)
        }
      );
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(() => {});
      }
    };
  }, [mode, isScanning]);

  function normalizeQrInput(input: string): string {
    const raw = input.trim();
    if (!raw) return raw;

    // If a QR encodes a URL like https://recruitlook.com/qr/checkin?code=XYZ
    // extract the ?code= value so staff scanning works seamlessly.
    if (/^https?:\/\//i.test(raw)) {
      try {
        const u = new URL(raw);
        const code = u.searchParams.get('code');
        if (code) return code;
      } catch {
        // ignore
      }
    }

    return raw;
  }

  async function handleScan(qrCode: string) {
    setScanResult(null);

    const normalized = normalizeQrInput(qrCode);

    const { data, error } = await supabase.rpc("scan_qr_code", {
      p_qr_code: normalized,
    });

    if (error) {
      setScanResult({ success: false, error: error.message });
    } else {
      setScanResult(data as ScanResult);
    }
  }

  function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (manualCode.trim()) {
      handleScan(manualCode.trim());
      setManualCode("");
    }
  }

  function getResultDisplay() {
    if (!scanResult) return null;

    if (scanResult.success) {
      let title = "";
      let detail = "";

      switch (scanResult.type) {
        case "registration":
          title = "✅ Checked In!";
          detail = scanResult.registrant_data?.coach?.name || 
                   scanResult.registrant_data?.team?.name || 
                   "Registration";
          break;
        case "ticket":
          title = "✅ Ticket Redeemed!";
          detail = scanResult.ticket_name || "Spectator Ticket";
          break;
        case "extra":
          title = "✅ Extra Redeemed!";
          detail = scanResult.extra_name || "Extra";
          break;
      }

      return (
        <div className="rounded-lg bg-green-100 p-4 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
          <p className="mt-2 text-xl font-bold text-green-800">{title}</p>
          <p className="text-green-700">{detail}</p>
        </div>
      );
    } else {
      return (
        <div className="rounded-lg bg-red-100 p-4 text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-600" />
          <p className="mt-2 text-xl font-bold text-red-800">❌ Failed</p>
          <p className="text-red-700">{scanResult.error}</p>
          {scanResult.redeemed_at && (
            <p className="mt-1 text-sm text-red-600">
              Previously used: {new Date(scanResult.redeemed_at).toLocaleString()}
            </p>
          )}
        </div>
      );
    }
  }

  return (
    <Card className="mx-auto max-w-md">
      <CardHeader>
        <CardTitle className="text-center">Event Check-In Scanner</CardTitle>
        <div className="flex justify-center gap-2">
          <Button
            variant={mode === "manual" ? "default" : "outline"}
            size="sm"
            onClick={() => { setMode("manual"); setIsScanning(false); }}
          >
            <Keyboard className="mr-1 h-4 w-4" /> Manual
          </Button>
          <Button
            variant={mode === "camera" ? "default" : "outline"}
            size="sm"
            onClick={() => { setMode("camera"); setIsScanning(true); }}
          >
            <Camera className="mr-1 h-4 w-4" /> Camera
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {mode === "manual" ? (
          <form onSubmit={handleManualSubmit} className="flex gap-2">
            <Input
              placeholder="Enter QR code (e.g., REG-abc123)"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
            />
            <Button type="submit">Scan</Button>
          </form>
        ) : (
          <div>
            {isScanning ? (
              <div id="qr-reader" className="mx-auto" />
            ) : (
              <Button onClick={() => setIsScanning(true)} className="w-full">
                Start Camera
              </Button>
            )}
          </div>
        )}

        {getResultDisplay()}

        {scanResult && (
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => setScanResult(null)}
          >
            Scan Next
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
